import Service, { service } from '@ember/service'
import type SessionService from 'district-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import config from 'district-ui-client/config/environment'
import type FeaturesService from 'district-ui-client/services/features'
import type ImpressionService from 'district-ui-client/services/impression'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'

export type ExternalRosteringSource = 'classlink' | 'ftp' | 'wonde' | 'clever'

export type RosteringDestination = 'manage-teachers' | 'manage-students' | 'manage-classes' | 'manage-admins'

const enabledSources: ExternalRosteringSource[] = ['classlink', 'ftp', 'wonde', 'clever']

// Wonde is rostering-only
const ssoSources: ExternalRosteringSource[] = ['classlink', 'ftp', 'clever']

export default class ExternalRosteringService extends Service {
  @service session!: SessionService
  @service intl!: IntlService
  @service features!: FeaturesService
  @service impression!: ImpressionService

  get isEnabled(): boolean {
    return this.usesExternalRostering && this.districtHasValidSource
  }

  get hidesTeacherLogin(): boolean {
    return this.usesExternalRostering && this.source !== undefined && ssoSources.includes(this.source)
  }

  get districtHasValidSource(): boolean {
    return this.source !== undefined && enabledSources.includes(this.source)
  }

  get districtHasInvalidSource(): boolean {
    return !this.districtHasValidSource
  }

  get usesExternalRostering(): boolean {
    return this.district.usesExternalRostering === true
  }

  get source(): ExternalRosteringSource | undefined {
    return this.district.externalRosteringSource
  }

  get isNewClasslink(): boolean {
    return this.source === 'classlink' && this.district.isNewClasslink === true
  }

  // 3P Roster enabled, and not rostered by an external integration
  get rostersInRosterApp(): boolean {
    return Boolean(this.district.oneRosterEnabled) && !this.isEnabled
  }

  private get district() {
    return this.session.currentDistrict
  }

  usageSummaryUrl(subscriptionType?: string) {
    if (!this.isEnabled) {
      return undefined
    }
    if (this.features.isDisabled(`dui_usage_summary_${this.source}`)) {
      return undefined
    }

    const redirectUrl = subscriptionType
      ? `/district/integration/${this.source}/status?tab=${subscriptionType}`
      : `/district/integration/${this.source}/status`
    return `${config.oneRosterUrl}login?return_to=${redirectUrl}`
  }

  manageUrl(subscriptionType?: string): string | undefined {
    if (!this.isEnabled) {
      return undefined
    }

    if (this.source === 'classlink') {
      if (this.isNewClasslink) {
        return `${config.integrationsUrl}login?return_to=/customers/districts/${this.district.id}/schools`
      }
      const redirectUrl = subscriptionType ? `/district/dashboard?tab=${subscriptionType}` : '/district/dashboard'
      return `${config.classlinkUrl}login?return_to=${redirectUrl}`
    }

    if (this.features.isDisabled(`dui_manage_integration_${this.source}`)) {
      return undefined
    }

    return `${config.integrationsUrl}login?return_to=/customers/districts/${this.district.id}/schools`
  }

  get displayName(): string {
    const source = this.district.externalRosteringSource
    if (source) {
      return this.intl.t(`externalRostering.displayName.${source}`)
    }
    return ''
  }

  logManageUrlClick() {
    this.impression.make(`manage-integration-${this.source}`)
  }

  logUsageSummaryUrlClick() {
    this.impression.make(`integration-usage-summary-${this.source}`)
  }

  // Builds a link into the OneRoster app. schoolId and destination pick the page to land on, program
  // is what OneRoster links back to on exit rostering. All are consumed on the OneRoster side.
  oneRosterUrl(schoolId: string, destination: RosteringDestination, program: SubscriptionType): string {
    // encoded, else the outer login url claims &program as its own
    const rosteringQuery = encodeURIComponent(`destination=${destination}&program=${program}`)
    return `${config.oneRosterUrl}login?return_to=/district/schools/${schoolId}/rostering?${rosteringQuery}`
  }
}

declare module '@ember/service' {
  interface Registry {
    'external-rostering': ExternalRosteringService
  }
}
